/**
 * HolidayEditPopover Component
 *
 * Quick edit popover for holidays in the calendar drawer.
 * Allows: color change, toggle visibility, delete, open full modal.
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { invalidateCalendarOverlay } from '@/lib/queryInvalidation';
import { Icon } from '@/components/ui/Icon';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { ICONS } from '@/constants/icons';
import { CalendarHoliday } from '@/types/holidays';
import { cn } from '@/utils/cn';
import api from '@/services/api';
import { toast } from '@/utils/toast';

interface HolidayEditPopoverProps {
  holiday: CalendarHoliday;
  onManageHolidays: () => void;
  children: React.ReactNode;
}

export const HolidayEditPopover = ({
  holiday,
  onManageHolidays,
  children,
}: HolidayEditPopoverProps) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const colorDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Color update mutation
  const colorMutation = useMutation({
    mutationFn: async (colorCode: string) => {
      const response = await api.patch(`/api/holidays/festivities/${holiday.festivityId}/color`, { colorCode });
      return response.data.data;
    },
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
      toast.success(t('color_updated'));
    },
    onError: () => {
      toast.error(t('failed_to_update_color'));
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: async (active: boolean) => {
      const response = await api.patch(`/api/holidays/festivities/${holiday.festivityId}/toggle`, { active });
      return response.data.data;
    },
    onSuccess: (_, active) => {
      invalidateCalendarOverlay(queryClient);
      toast.success(active ? t('holidays_shown') : t('holidays_hidden'));
    },
    onError: () => {
      toast.error(t('failed_to_toggle_holidays'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/api/holidays/festivities/${holiday.festivityId}`);
    },
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
      toast.success(t('holidays_removed'));
      setOpen(false);
    },
    onError: () => {
      toast.error(t('failed_to_delete_holidays'));
    },
  });

  const handleColorChange = useCallback((color: string) => {
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      // Debounce API calls - only save after user stops picking for 500ms
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
      colorDebounceRef.current = setTimeout(() => {
        colorMutation.mutate(color);
      }, 500);
    }
  }, [colorMutation]);

  const handleDelete = () => {
    if (showDeleteConfirm) {
      deleteMutation.mutate();
    } else {
      setShowDeleteConfirm(true);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) setShowDeleteConfirm(false);
    }}>
      <Popover.Trigger asChild>
        {children}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-popover-top bg-white rounded-xl shadow-xl border border-gray-200 w-72 animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="start"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">{holiday.localName}</h3>
                <p className="text-xs text-gray-500">{holiday.countryName}</p>
              </div>
              <Popover.Close className="p-1 hover:bg-gray-100 rounded">
                <Icon name={ICONS.CLOSE} size="xs" ariaHidden />
              </Popover.Close>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('color_for_country', { country: holiday.countryName })}
              </label>
              <ColorPicker
                value={holiday.colorCode || '#FFD700'}
                onChange={handleColorChange}
              />
              <p className="text-xs text-gray-400 mt-1">{t('color_affects_all_holidays')}</p>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              {/* Toggle Visibility */}
              <button
                onClick={() => toggleMutation.mutate(false)}
                disabled={toggleMutation.isPending}
                className="w-full flex items-start gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Icon name={ICONS.EYE_SLASH} size="sm" ariaHidden className="flex-shrink-0 mt-0.5" />
                {t('hide_country_holidays', { country: holiday.countryName })}
              </button>

              {/* Manage Holidays */}
              <button
                onClick={() => {
                  setOpen(false);
                  onManageHolidays();
                }}
                className="w-full flex items-start gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Icon name={ICONS.SETTINGS} size="sm" ariaHidden className="flex-shrink-0 mt-0.5" />
                {t('manage_all_holidays')}
              </button>

              {/* Delete */}
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className={cn(
                  'w-full flex items-start gap-2 px-3 py-2 text-sm text-left rounded-lg transition-colors',
                  showDeleteConfirm
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'text-red-600 hover:bg-red-50'
                )}
              >
                <Icon name={ICONS.TRASH} size="sm" ariaHidden className="flex-shrink-0 mt-0.5" />
                {showDeleteConfirm
                  ? t('click_to_confirm_delete')
                  : t('delete_country_holidays', { country: holiday.countryName })
                }
              </button>
            </div>
          </div>

          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
